import { Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { addDaysLocal, todayLocal } from '@control-center/shared';

import { PillButton } from '../../components/clients/PillButton';
import { Select } from '../../components/clients/form-primitives';
import { MrrByProductChart } from '../../components/reports/MrrByProductChart';
import { MrrSeriesChart } from '../../components/reports/MrrSeriesChart';
import { downloadMrrCsv } from '../../lib/reports-api';
import { useMrrByProduct, useMrrSeries } from '../../hooks/use-reports';

type Period = '30d' | '90d' | '6m' | '12m';

const PERIOD_DAYS: Record<Period, number> = { '30d': 30, '90d': 90, '6m': 182, '12m': 365 };
const brl = (v: number | null): string =>
  v === null
    ? '—'
    : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const pct = (v: number | null): string => (v === null ? '—' : `${(v * 100).toFixed(1)}%`);

/** Story 12.11 — relatórios financeiros (MRR/ARR série + churn + LTV + breakdown por produto). */
export function FinancialReportsPage(): JSX.Element {
  const [period, setPeriod] = useState<Period>('12m');
  const [downloading, setDownloading] = useState(false);

  const range = useMemo(() => {
    const to = todayLocal();
    const from = addDaysLocal(to, -PERIOD_DAYS[period]);
    const groupBy = PERIOD_DAYS[period] <= 90 ? ('day' as const) : ('month' as const);
    return { from, to, groupBy };
  }, [period]);

  const seriesQ = useMrrSeries(range);
  const byProductQ = useMrrByProduct();

  const series = seriesQ.data?.data.series ?? [];
  const latest = series.length > 0 ? series[series.length - 1] : null;

  async function handleExport() {
    setDownloading(true);
    try {
      await downloadMrrCsv({ from: range.from, to: range.to });
    } catch {
      toast.error('Falha ao exportar CSV.');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Relatórios financeiros</h1>
          <p className="mt-1 text-sm text-muted">
            MRR, ARR, churn e LTV da carteira — lidos dos snapshots diários (cron 00:30 BRT).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
            <option value="6m">Últimos 6 meses</option>
            <option value="12m">Últimos 12 meses</option>
          </Select>
          <PillButton
            onClick={() => void handleExport()}
            disabled={downloading}
            icon={<Download size={13} strokeWidth={2} />}
          >
            {downloading ? 'Exportando…' : 'Exportar CSV'}
          </PillButton>
        </div>
      </div>

      {/* AC9 — 4 cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="MRR atual" value={brl(latest?.mrrTotal ?? null)} />
        <MetricCard label="ARR atual" value={brl(latest?.arrTotal ?? null)} />
        <MetricCard
          label="Churn mensal"
          value={pct(latest?.churnRate ?? null)}
          note="Inclui cancelamentos voluntários e involuntários. Sem distinção por motivo."
        />
        <MetricCard
          label="LTV estimado"
          value={brl(latest?.ltvEstimate ?? null)}
          note="LTV estimado = ARPU ÷ Churn Rate mensal (proxy simples)."
        />
      </div>

      {seriesQ.isError && (
        <div className="rounded-[12px] border border-bad/40 bg-bad/10 p-4 text-sm text-ink">
          Erro ao carregar a série temporal.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {seriesQ.isLoading ? (
          <div className="h-80 animate-pulse rounded-lg border border-line bg-panel-2" />
        ) : (
          <MrrSeriesChart points={series} />
        )}
        {byProductQ.isLoading ? (
          <div className="h-80 animate-pulse rounded-lg border border-line bg-panel-2" />
        ) : (
          <MrrByProductChart data={byProductQ.data?.data.byProduct ?? []} />
        )}
      </div>

      <p className="text-[11px] text-muted-3">
        Análise de coorte/retenção por safra ainda não disponível. CAC e payback não calculados (sem
        fonte de custo de aquisição).
      </p>
    </div>
  );
}

function MetricCard({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}): JSX.Element {
  return (
    <div className="rounded-[12px] border border-line bg-panel p-4" title={note}>
      <div className="text-[12px] text-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight text-ink tnum">{value}</div>
      {note && <div className="mt-1 text-[10px] leading-snug text-muted-3">{note}</div>}
    </div>
  );
}
